import { useState, useEffect, useRef } from 'react';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Github, Twitter, Linkedin, Mail, ChevronRight, Brain, Radar, Shield, Zap, Globe, BarChart3, Lock, Network, Eye, Bot, Target, FileSearch, Clock, Award, ArrowRight, Check, Activity, Play, X, Menu, GitMerge, TrendingUp, Send, User, Building2, ChevronLeft, ChevronRight as ChevronRightIcon, MapPin, Phone, Briefcase, BookOpen, FileText, Video, GraduationCap, BellRing, Layers3, Sparkles } from 'lucide-react';
import { API_BASE_URL } from './config';

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };
const staggerContainer = { visible: { transition: { staggerChildren: 0.12 } } };

type LearnResource = {
  icon: React.ElementType;
  tag: string;
  title: string;
  desc: string;
  linkLabel: string;
  highlight: string;
  bullets: string[];
};

// Single source of truth for primary navigation — used by desktop + mobile menus.
const NAV_LINKS: { href: string; id: string; label: string; icon: React.ElementType | null }[] = [
  { href: '#demo', id: 'demo', label: 'Demo', icon: Play },
  { href: '#features', id: 'features', label: 'Platform', icon: null },
  { href: '#osint', id: 'osint', label: 'OSINT Suites', icon: null },
  { href: '#learn', id: 'learn', label: 'Learn', icon: BookOpen },
  { href: '#pricing', id: 'pricing', label: 'Pricing', icon: null },
  { href: '#about', id: 'about', label: 'About', icon: null },
  { href: '#platform', id: 'platform', label: 'Technology', icon: null },
  { href: '#contact', id: 'contact', label: 'Contact', icon: null },
];

// Country list for the demo request form (India first, then alphabetical, Other last).
const DEMO_COUNTRIES = [
  'India',
  'Australia', 'Bangladesh', 'Brazil', 'Canada', 'France', 'Germany', 'Indonesia',
  'Israel', 'Italy', 'Japan', 'Kenya', 'Malaysia', 'Mexico', 'Netherlands',
  'New Zealand', 'Nigeria', 'Pakistan', 'Philippines', 'Qatar', 'Saudi Arabia',
  'Singapore', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden',
  'Switzerland', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Other',
];

// Carousel feature panels — each shown for 3 seconds
const CAROUSEL_FEATURES = [
  {
    tag: 'AI DETECTION',
    gradient: 'from-cyan-500 to-blue-600',
    glow: 'rgba(6,182,212,0.25)',
    icon: Brain,
    title: 'Real-Time Misinformation Detection',
    desc: 'Transformer AI classifies false narratives across 14 languages in under 200ms.',
    metrics: [
      { label: 'Accuracy', value: '97.3%', color: 'text-cyan-400' },
      { label: 'Languages', value: '14', color: 'text-blue-400' },
      { label: 'Latency', value: '<200ms', color: 'text-emerald-400' },
    ],
    bars: [
      { label: 'Narrative Detection', pct: 97, color: 'from-cyan-500 to-blue-500' },
      { label: 'Source Credibility', pct: 91, color: 'from-blue-500 to-indigo-500' },
      { label: 'Bot Amplification', pct: 95, color: 'from-indigo-500 to-violet-500' },
    ],
    alert: { level: 'HIGH', label: 'Coordinated narrative detected', color: 'border-amber-500/40 bg-amber-500/10 text-amber-300' },
  },
  {
    tag: 'BOT NETWORK',
    gradient: 'from-rose-500 to-orange-500',
    glow: 'rgba(244,63,94,0.22)',
    icon: Bot,
    title: 'Bot Network Identification',
    desc: 'Multi-layer behavioral analysis exposes coordinated bot farms and amplification networks.',
    metrics: [
      { label: 'Precision', value: '95%', color: 'text-rose-400' },
      { label: 'Signals', value: '40+', color: 'text-orange-400' },
      { label: 'Detection', value: '<30s', color: 'text-emerald-400' },
    ],
    bars: [
      { label: 'Behavioral Fingerprint', pct: 95, color: 'from-rose-500 to-orange-500' },
      { label: 'Network Clustering', pct: 88, color: 'from-orange-500 to-amber-500' },
      { label: 'Temporal Burst Score', pct: 92, color: 'from-amber-500 to-yellow-500' },
    ],
    alert: { level: 'CRITICAL', label: '847 bot accounts neutralized', color: 'border-rose-500/40 bg-rose-500/10 text-rose-300' },
  },
  {
    tag: 'PROPAGATION GRAPH',
    gradient: 'from-emerald-500 to-teal-500',
    glow: 'rgba(16,185,129,0.22)',
    icon: Network,
    title: 'Misinformation Propagation Tree',
    desc: 'Live force-directed graph maps every retweet, share, and re-post chain across platforms.',
    metrics: [
      { label: 'Node types', value: '8', color: 'text-emerald-400' },
      { label: 'Refresh', value: 'Live', color: 'text-teal-400' },
      { label: 'Max nodes', value: '10K', color: 'text-cyan-400' },
    ],
    bars: [
      { label: 'Cross-platform Spread', pct: 89, color: 'from-emerald-500 to-teal-500' },
      { label: 'Super-spreader Detection', pct: 94, color: 'from-teal-500 to-cyan-500' },
      { label: 'Hub Identification', pct: 91, color: 'from-cyan-500 to-blue-500' },
    ],
    alert: { level: 'MEDIUM', label: 'Cascade mapped across 6 platforms', color: 'border-teal-500/40 bg-teal-500/10 text-teal-300' },
  },
  {
    tag: 'THREAT SCORING',
    gradient: 'from-amber-500 to-red-500',
    glow: 'rgba(245,158,11,0.22)',
    icon: Shield,
    title: 'Composite Threat Risk Engine',
    desc: '12-signal composite score combines virality, bot ratio, toxicity, and geo-reach into one actionable threat vector.',
    metrics: [
      { label: 'Risk signals', value: '12', color: 'text-amber-400' },
      { label: 'Alert levels', value: '3', color: 'text-red-400' },
      { label: 'Recalibration', value: 'Weekly', color: 'text-orange-400' },
    ],
    bars: [
      { label: 'Virality Velocity', pct: 86, color: 'from-amber-500 to-orange-500' },
      { label: 'Toxicity Score', pct: 79, color: 'from-orange-500 to-red-500' },
      { label: 'Geo Reach Index', pct: 93, color: 'from-red-500 to-rose-500' },
    ],
    alert: { level: 'CRITICAL', label: 'Threat score: 0.91 — escalating', color: 'border-red-500/40 bg-red-500/10 text-red-300' },
  },
  {
    tag: 'MEDIA FORENSICS',
    gradient: 'from-violet-500 to-indigo-600',
    glow: 'rgba(139,92,246,0.22)',
    icon: Eye,
    title: 'Deepfake & Media Forensics',
    desc: 'GAN-fingerprint detection authenticates images, videos, and documents with cryptographic-grade accuracy.',
    metrics: [
      { label: 'Deepfake accuracy', value: '94%', color: 'text-violet-400' },
      { label: 'Image index', value: '2B+', color: 'text-indigo-400' },
      { label: 'Formats', value: '12+', color: 'text-purple-400' },
    ],
    bars: [
      { label: 'GAN Fingerprint Score', pct: 94, color: 'from-violet-500 to-purple-500' },
      { label: 'EXIF Metadata Match', pct: 98, color: 'from-purple-500 to-indigo-500' },
      { label: 'Reverse Image Match', pct: 87, color: 'from-indigo-500 to-blue-500' },
    ],
    alert: { level: 'HIGH', label: 'Deepfake video flagged — 94.2% confidence', color: 'border-violet-500/40 bg-violet-500/10 text-violet-300' },
  },
  {
    tag: 'BRAND PROTECTION',
    gradient: 'from-pink-500 to-fuchsia-600',
    glow: 'rgba(236,72,153,0.22)',
    icon: Award,
    title: 'Brand & Reputation Shield',
    desc: 'Monitor brand mentions, trademark misuse, impersonation accounts, and reputation attacks across 500+ channels in real time.',
    metrics: [
      { label: 'Channels', value: '500+', color: 'text-pink-400' },
      { label: 'Detection', value: '<60s', color: 'text-fuchsia-400' },
      { label: 'Languages', value: '20+', color: 'text-purple-400' },
    ],
    bars: [
      { label: 'Impersonation Detection', pct: 93, color: 'from-pink-500 to-fuchsia-500' },
      { label: 'Trademark Misuse Radar', pct: 88, color: 'from-fuchsia-500 to-purple-500' },
      { label: 'Sentiment Shift Alert', pct: 91, color: 'from-purple-500 to-violet-500' },
    ],
    alert: { level: 'HIGH', label: 'Brand impersonation attempt detected', color: 'border-pink-500/40 bg-pink-500/10 text-pink-300' },
    comingSoon: true,
  },
  {
    tag: 'BUSINESS INTELLIGENCE',
    gradient: 'from-sky-500 to-blue-600',
    glow: 'rgba(14,165,233,0.22)',
    icon: BarChart3,
    title: 'Competitive Market Signal Intelligence',
    desc: 'Aggregate public signals — competitor narratives, market sentiment, regulatory discourse, and industry trends — into actionable intelligence dashboards.',
    metrics: [
      { label: 'Signal sources', value: '1K+', color: 'text-sky-400' },
      { label: 'Refresh', value: '5 min', color: 'text-blue-400' },
      { label: 'Industries', value: '30+', color: 'text-indigo-400' },
    ],
    bars: [
      { label: 'Competitor Narrative Tracking', pct: 85, color: 'from-sky-500 to-blue-500' },
      { label: 'Market Sentiment Index', pct: 90, color: 'from-blue-500 to-indigo-500' },
      { label: 'Regulatory Signal Radar', pct: 87, color: 'from-indigo-500 to-violet-500' },
    ],
    alert: { level: 'MEDIUM', label: 'Competitor campaign surge detected', color: 'border-sky-500/40 bg-sky-500/10 text-sky-300' },
    comingSoon: true,
  },
];

type FeatureItem = { icon: React.ElementType; title: string; desc: string; gradient: string; tag: string; details: string[]; stats: { label: string; value: string }[]; highlight: string; };
type OsintItem = { num: string; suite: string; tag: string; icon: React.ElementType; gradient: string; border: string; items: readonly string[]; desc: string; highlight: string; details: string[]; stats: { label: string; value: string }[]; };

const FEATURES: FeatureItem[] = [
  {
    icon: Brain, title: 'AI Misinformation Engine', tag: 'CORE AI', gradient: 'from-cyan-500 to-blue-600',
    desc: 'Transformer-based NLP models trained on 50M+ labeled samples detect false narratives, manipulated media, and coordinated inauthentic behavior with 97.3% accuracy.',
    highlight: 'Industry-leading 97.3% detection accuracy via ensemble transformers',
    details: [
      'Ensemble of BERT, RoBERTa, and XLM-R models with majority-vote gating',
      'Trained on 50M+ labeled samples across 14 languages',
      'Detects coordinated inauthentic behavior and narrative injection campaigns',
      'Identifies manipulated media metadata, recycled imagery, and deepfake captions',
      'Real-time re-ranking of model confidence with adversarial robustness checks',
    ],
    stats: [{ label: 'Accuracy', value: '97.3%' }, { label: 'Languages', value: '14' }, { label: 'Training samples', value: '50M+' }],
  },
  {
    icon: Radar, title: 'Real-Time Signal Intelligence', tag: 'STREAMING', gradient: 'from-purple-500 to-pink-600',
    desc: 'Continuous ingestion from X, Reddit, Facebook, YouTube, and 500+ news sources via Kafka streaming. Sub-second latency from post to classification.',
    highlight: 'Sub-second post-to-classification latency at massive scale',
    details: [
      'Apache Kafka producer–consumer pipeline with auto-scaling partitions',
      'Async collectors for X/Twitter, Reddit, YouTube, Facebook, and 500+ RSS news feeds',
      'India-specific OSINT sources: NDTV, ANI, Hindu, Times of India, BJP/Congress feeds',
      'Deduplication and source credibility scoring before downstream AI processing',
      'Dead-letter queue + automatic retry for collector failures',
    ],
    stats: [{ label: 'Sources', value: '500+' }, { label: 'Latency', value: '<1s' }, { label: 'Throughput', value: '10K/min' }],
  },
  {
    icon: Bot, title: 'Bot Network Detection', tag: 'ML PIPELINE', gradient: 'from-rose-500 to-orange-500',
    desc: 'Multi-layer ensemble model identifies coordinated bot networks using behavioral patterns, temporal analysis, and network graph clustering with 95% precision.',
    highlight: '95% precision bot identification with zero-day behavioral signatures',
    details: [
      'Behavioral fingerprinting: posting cadence, copy-paste similarity, cross-platform pivoting',
      'Temporal burst analysis detects coordinated amplification events',
      'Graph clustering identifies botnet hubs and relay accounts',
      'Account age, follower ratio, and bio entropy scoring',
      'Continuously updated signatures for new bot-farm operational patterns',
    ],
    stats: [{ label: 'Precision', value: '95%' }, { label: 'Signal features', value: '40+' }, { label: 'Detection lag', value: '<30s' }],
  },
  {
    icon: Network, title: 'Network Graph Analysis', tag: 'ANALYTICS', gradient: 'from-emerald-500 to-teal-500',
    desc: 'Visualize information cascades and amplification networks. Map how misinformation spreads across platforms, identify super-spreaders and coordination hubs in real time.',
    highlight: 'Live force-directed graph of every retweet, share, and re-post chain',
    details: [
      'Force-directed propagation graph with directional particle flow',
      'Super-spreader identification via betweenness centrality scoring',
      'Cross-platform edge linking (X → Reddit → YouTube cascade chains)',
      'Coordination hub detection using community detection algorithms',
      'Exportable graph snapshots for incident reporting and evidence preservation',
    ],
    stats: [{ label: 'Node types', value: '5' }, { label: 'Graph refresh', value: 'Real-time' }, { label: 'Max nodes', value: '10K' }],
  },
  {
    icon: Shield, title: 'Threat Risk Scoring', tag: 'RISK ENGINE', gradient: 'from-amber-500 to-red-500',
    desc: 'Composite risk engine combining virality metrics, bot amplification, sentiment toxicity, and source credibility into a single 0–1 threat vector score.',
    highlight: 'Single 0–1 threat score ingesting 12 independent risk signals',
    details: [
      '12-signal composite: virality velocity, bot ratio, toxicity, credibility, geo-reach, and more',
      'Weighted ensemble model recalibrated weekly against confirmed incident outcomes',
      'Automatic alert escalation at configurable risk thresholds (0.6 / 0.8 / 0.95)',
      'Risk trajectory trending — detects score acceleration before viral peak',
      'Operator-tunable weights to prioritize domain-specific risk factors',
    ],
    stats: [{ label: 'Risk signals', value: '12' }, { label: 'Alert levels', value: '3' }, { label: 'Recalibration', value: 'Weekly' }],
  },
  {
    icon: Eye, title: 'Deepfake & Media Forensics', tag: 'FORENSICS', gradient: 'from-indigo-500 to-violet-600',
    desc: 'AI-powered image and video analysis detects synthetic media, manipulated footage, and out-of-context imagery using state-of-the-art GAN detection models.',
    highlight: 'GAN-fingerprint detection with reverse image search and EXIF forensics',
    details: [
      'GAN fingerprint analysis for image and video authenticity verification',
      'Face-swap and lip-sync deepfake detection via temporal inconsistency modeling',
      'Reverse image search against 2B+ indexed images for context verification',
      'EXIF metadata extraction and GPS coordinate cross-referencing',
      'Out-of-context imagery detection using caption–image semantic alignment scoring',
    ],
    stats: [{ label: 'Deepfake accuracy', value: '94%' }, { label: 'Image index', value: '2B+' }, { label: 'Formats', value: 'JPG/MP4/GIF' }],
  },
  {
    icon: GitMerge, title: 'Narrative Mapping', tag: 'NARRATIVE INTEL', gradient: 'from-fuchsia-500 to-purple-600',
    desc: 'Multi-agent clustering maps global narratives across threat intensity, political polarization, intent, and virality. Live visualization of active narrative clusters and automatic extraction of core claims.',
    highlight: 'Live narrative cluster map across 6 dimensions updated every 60 seconds',
    details: [
      'Multi-agent LLM pipeline extracts and clusters core claims from raw posts',
      'Narrative lifecycle tracking: emergence → amplification → mainstream → decay',
      'Political polarization scoring per narrative using stance detection models',
      'Intent classification: organic spread vs. coordinated seeding vs. state-actor operation',
      'Virality trajectory prediction — flags narratives before they reach critical mass',
    ],
    stats: [{ label: 'Cluster refresh', value: '60s' }, { label: 'Dimensions', value: '6' }, { label: 'Claim extraction', value: 'Auto' }],
  },
  {
    icon: TrendingUp, title: 'Sentiment Analysis', tag: 'ANALYTICS', gradient: 'from-teal-500 to-cyan-500',
    desc: 'Real-time multilingual tracking of sentiment trends, toxicity levels, and emotional polarization across social and news platforms to predict narrative escalation and crisis momentum.',
    highlight: 'Multilingual sentiment in 14 languages with crisis momentum forecasting',
    details: [
      'Fine-tuned XLM-R model for multilingual sentiment across 14 languages',
      'Granular emotion detection: anger, fear, disgust, surprise, sadness, and joy',
      'Toxicity scoring using trained classifier on 2M+ annotated social posts',
      'Emotional polarization heatmap showing left–right sentiment divergence by topic',
      'Crisis momentum score: predicts escalation window 2–6 hours ahead of viral peak',
    ],
    stats: [{ label: 'Languages', value: '14' }, { label: 'Emotion classes', value: '6' }, { label: 'Forecast window', value: '2–6 hrs' }],
  },
  {
    icon: Award, title: 'Brand & Reputation Shield', tag: 'BRAND PROTECTION', gradient: 'from-pink-500 to-fuchsia-600',
    desc: 'Continuously monitor brand mentions, trademark misuse, impersonation accounts, and coordinated reputation attacks across 500+ channels — with real-time sentiment-shift alerting and executive-impersonation detection.',
    highlight: 'Impersonation, trademark misuse, and reputation attacks caught in <60s',
    details: [
      'Real-time brand mention monitoring across 500+ social, news, and forum channels',
      'Impersonation & fake-account detection with executive-name and logo matching',
      'Trademark and copyright misuse radar across marketplaces and ad networks',
      'Sentiment-shift alerting that flags coordinated reputation attacks before they trend',
      'Per-brand risk dashboards with platform breakdown and resolvable alert workflows',
    ],
    stats: [{ label: 'Channels', value: '500+' }, { label: 'Detection', value: '<60s' }, { label: 'Languages', value: '20+' }],
  },
  {
    icon: BarChart3, title: 'Business Intelligence', tag: 'MARKET SIGNALS', gradient: 'from-sky-500 to-blue-600',
    desc: 'Aggregate public signals — competitor narratives, leadership changes, regulatory discourse, supply-chain risk, and M&A activity — into actionable market intelligence with predictive risk forecasting and executive briefings.',
    highlight: 'Competitor, regulatory, supply-chain & M&A intelligence in one fused feed',
    details: [
      'Competitor mention tracking with sentiment, reach, and leadership-change detection',
      'Regulatory and policy-discourse radar across 30+ industries and jurisdictions',
      'Supply-chain and dark-web risk signals fused into a single relevance-ranked feed',
      'Predictive risk forecasting (7/30/90-day) with scenario simulation and war-gaming',
      'Auto-generated executive briefings, watchlists, and notification rules',
    ],
    stats: [{ label: 'Signal sources', value: '1K+' }, { label: 'Refresh', value: '5 min' }, { label: 'Industries', value: '30+' }],
  },
];

const OSINT_SUITES: OsintItem[] = [
  {
    num: '01', suite: 'Cyber Threat Intel', tag: 'SUITE 1',
    icon: Globe, gradient: 'from-cyan-500 to-blue-600', border: 'border-cyan-500/20',
    desc: 'Real-time cyber intelligence engine that traces IP infrastructure, uncovers hosting risks, and delivers instant abuse scoring and threat classification from any URL, domain, or digital footprint.',
    highlight: 'Full IP-to-infrastructure chain traced in under 2 seconds',
    items: ['IP Geolocation & Abuse Scoring', 'DNS / WHOIS Lookup', 'Malware & Phishing Flags', 'ASN & Hosting Intel'],
    details: [
      'IP geolocation with abuse confidence score via AbuseIPDB and MaxMind datasets',
      'Full DNS chain resolution — A, MX, NS, TXT records with historical delta tracking',
      'WHOIS registration history, registrar reputation, and domain age scoring',
      'ASN ownership lookup identifying bulletproof hosting and high-risk providers',
      'VirusTotal and URLScan integration for live malware and phishing classification',
    ],
    stats: [{ label: 'Lookup time', value: '<2s' }, { label: 'Data sources', value: '8+' }, { label: 'Risk signals', value: '15' }],
  },
  {
    num: '02', suite: 'Identity Recon', tag: 'SUITE 2',
    icon: Eye, gradient: 'from-purple-500 to-pink-600', border: 'border-purple-500/20',
    desc: 'Deep identity intelligence platform that cross-links emails, phone numbers, usernames, and social profiles to reveal hidden relationships and build complete entity risk profiles in seconds.',
    highlight: 'Cross-platform entity graph built from a single email or username',
    items: ['Email Breach Check (HIBP)', 'Phone Carrier Lookup', 'Social Profile Pivoting', 'Username Cross-linking'],
    details: [
      'Have I Been Pwned breach database — checks email against 12B+ exposed credentials',
      'Phone number carrier lookup with line-type classification (VoIP, burner, MVNO)',
      'Social profile pivoting across 30+ platforms from a single username or handle',
      'Cross-platform account linkage graph revealing shared infrastructure or personas',
      'Entity risk profile scoring combining breach exposure, VoIP flags, and activity patterns',
    ],
    stats: [{ label: 'Platforms', value: '30+' }, { label: 'Breach records', value: '12B+' }, { label: 'Profile time', value: '<5s' }],
  },
  {
    num: '03', suite: 'Compliance & Sanctions', tag: 'SUITE 3',
    icon: Shield, gradient: 'from-amber-500 to-orange-600', border: 'border-amber-500/20',
    desc: 'Automated global compliance engine that screens against sanctions lists, verifies businesses, monitors adverse media, and flags high-risk entities to ensure regulatory safety at enterprise scale.',
    highlight: 'Real-time OFAC, UN, EU, and FATF sanctions screening in one pass',
    items: ['OFAC Sanctions Screening', 'Business Registry Lookup', 'Adverse Media Monitoring', 'Entity Risk Classification'],
    details: [
      'Simultaneous screening against OFAC SDN, UN Security Council, EU, and FATF lists',
      'Business registry lookup across 190+ jurisdictions with UBO chain resolution',
      'Adverse media monitoring — NLP-scanned news flagging corruption, fraud, and litigation',
      'PEP (Politically Exposed Person) classification with relationship-graph proximity scoring',
      'Audit-ready compliance reports with timestamp, source, and match-confidence metadata',
    ],
    stats: [{ label: 'Sanctions lists', value: '4+' }, { label: 'Jurisdictions', value: '190+' }, { label: 'Screen time', value: '<3s' }],
  },
  {
    num: '04', suite: 'Media Forensics', tag: 'SUITE 4',
    icon: FileSearch, gradient: 'from-emerald-500 to-teal-500', border: 'border-emerald-500/20',
    desc: 'Digital media verification suite that authenticates images, videos, and documents, extracts forensic metadata, preserves evidence chains, and detects manipulation with cryptographic-grade accuracy.',
    highlight: 'SHA-256 evidence hashing + GAN deepfake scoring on every media asset',
    items: ['Archive.org Snapshot Check', 'SHA-256 Evidence Hashing', 'JPEG EXIF GPS Extraction', 'GAN-based Deepfake Score'],
    details: [
      'Wayback Machine snapshot timeline — reveals when content first appeared online',
      'SHA-256 cryptographic hashing of all media files for tamper-evident evidence chains',
      'Full EXIF metadata extraction including GPS coordinates, device model, and timestamps',
      'GAN fingerprint scoring for image and video authenticity with confidence percentile',
      'JPEG block artifact analysis and compression history detection for manipulation flags',
    ],
    stats: [{ label: 'Hash algorithm', value: 'SHA-256' }, { label: 'Media formats', value: '12+' }, { label: 'Deepfake accuracy', value: '94%' }],
  },
  {
    num: '05', suite: 'Geo & Transport Intel', tag: 'SUITE 5',
    icon: Radar, gradient: 'from-rose-500 to-red-600', border: 'border-rose-500/20',
    desc: 'Live geospatial and mobility intelligence layer that tracks aircraft, vessels, and physical assets while delivering real-time proximity alerts to crisis zones and high-threat locations worldwide.',
    highlight: 'Live ADS-B flight + AIS vessel tracking with crisis-zone proximity alerts',
    items: ['Live Flight Tracking (ADS-B)', 'Vessel AIS Monitoring', 'Crisis Zone Proximity Alert', 'Nominatim Geo-resolution'],
    details: [
      'ADS-B flight data ingestion with altitude, heading, speed, and squawk code parsing',
      'AIS vessel position monitoring covering 300K+ active maritime assets globally',
      'Configurable geofences around crisis zones — auto-alert on entry within defined radius',
      'Nominatim reverse geocoding resolving coordinates to street-level place names',
      'Historical movement replay for incident timeline reconstruction and evidence export',
    ],
    stats: [{ label: 'Vessels tracked', value: '300K+' }, { label: 'Alert latency', value: '<10s' }, { label: 'Coverage', value: 'Global' }],
  },
  {
    num: '06', suite: 'Neural AI Forensics', tag: 'SUITE 6',
    icon: Brain, gradient: 'from-violet-500 to-indigo-600', border: 'border-violet-500/20',
    desc: 'Next-generation AI content forensics that detects deepfakes, voice clones, synthetic text, and machine-generated media while attributing content to specific generative models with high confidence.',
    highlight: 'Model attribution to GPT, Midjourney, Sora, and 20+ generative AI systems',
    items: ['Deepfake Video Detection', 'Voice Clone Fingerprinting', 'AI-Generated Text Scoring', 'Model Attribution (GPT / Midjourney)'],
    details: [
      'Temporal inconsistency modeling detects face-swap and lip-sync deepfake artifacts',
      'Voice clone fingerprinting via spectral analysis and prosody deviation scoring',
      'AI-generated text classifier trained on outputs from GPT-4, Claude, Gemini, and Llama',
      'Model attribution engine identifies the likely generative system behind synthetic content',
      'Confidence-scored forensic reports exportable for legal proceedings and regulatory filings',
    ],
    stats: [{ label: 'AI models tracked', value: '20+' }, { label: 'Text accuracy', value: '93%' }, { label: 'Voice accuracy', value: '91%' }],
  },
];

const LEARN_RESOURCES = [
  {
    icon: GraduationCap,
    tag: 'START HERE',
    title: 'Narrative Risk Fundamentals',
    desc: 'A fast, plain-language primer for leadership teams that need to understand how narrative attacks form, spread, and convert into real-world pressure.',
    linkLabel: 'Read the primer',
    highlight: 'Best for executives, policy leaders, and comms heads who need a rapid baseline on narrative risk.',
    bullets: [
      'Defines narrative attacks, pressure campaigns, and cross-platform amplification in practical terms.',
      'Shows how false claims move from fringe communities into mainstream conversation.',
      'Explains which early signals matter most when response time is limited.',
    ],
  },
  {
    icon: Layers3,
    tag: 'PLAYBOOKS',
    title: 'Crisis Response Playbooks',
    desc: 'Step-by-step operator playbooks for election integrity, brand attacks, communal violence rumors, deepfake response, and coordinated bot surges.',
    linkLabel: 'Explore playbooks',
    highlight: 'Built for teams that need operational sequencing, not abstract guidance.',
    bullets: [
      'Incident-ready steps for detection, triage, escalation, and response ownership.',
      'Clear roles for analysts, public affairs, leadership, and outside stakeholders.',
      'Templates for evidence capture and decision logs during active incidents.',
    ],
  },
  {
    icon: FileText,
    tag: 'REPORTS',
    title: 'Threat Briefing Library',
    desc: 'Analyst-ready briefs covering recurring misinformation patterns, platform behaviors, escalation signals, and regional threat archetypes.',
    linkLabel: 'Browse briefings',
    highlight: 'Turns noisy event monitoring into reusable institutional knowledge.',
    bullets: [
      'Pattern briefs on rumor recycling, manipulated media, and influence escalation tactics.',
      'Regional and sector-specific briefing structures for repeatable reporting.',
      'Leadership-ready summaries that compress detection into decision support.',
    ],
  },
  {
    icon: Video,
    tag: 'WEBINARS',
    title: 'Operator Walkthrough Sessions',
    desc: 'Recorded platform walkthroughs and expert sessions on claim extraction, geospatial triage, evidence packaging, and incident escalation.',
    linkLabel: 'Watch sessions',
    highlight: 'Ideal for onboarding analysts and response teams onto a shared operating method.',
    bullets: [
      'Guided walkthroughs of investigation flows inside the platform.',
      'Deep dives into escalation logic, geo scoping, and alert interpretation.',
      'Shorter tracks for new operators and advanced tracks for team leads.',
    ],
  },
  {
    icon: BellRing,
    tag: 'ALERTING',
    title: 'Detection Signal Guides',
    desc: 'Reference guides explaining the signals behind bot probability, virality acceleration, sentiment shocks, and narrative coordination scores.',
    linkLabel: 'See signal guides',
    highlight: 'Useful when teams want to understand why the platform is surfacing a threat.',
    bullets: [
      'Explains the logic behind risk signals without exposing internal IP or model internals.',
      'Helps teams calibrate thresholds for urgency, severity, and response ownership.',
      'Connects alert math to real operator decisions in the field.',
    ],
  },
  {
    icon: Sparkles,
    tag: 'FIELD NOTES',
    title: 'Use-Case Stories',
    desc: 'Compact examples showing how agencies, media teams, and enterprise risk desks use CrisisShield to move from noisy feeds to decisive action.',
    linkLabel: 'View use cases',
    highlight: 'Shows practical outcomes by team type rather than generic marketing claims.',
    bullets: [
      'Government response examples focused on public safety and regional escalation.',
      'Enterprise examples focused on brand, executive, and supply-chain risk.',
      'Media examples focused on verification speed and newsroom decision support.',
    ],
  },
] satisfies LearnResource[];

const PRICING_PLANS = [
  {
    tier: 'Response Core',
    audience: 'For compact intel and communications teams',
    summary: 'Essential monitoring, alerting, and analyst workflows for teams that need credible early warning without a heavy rollout.',
    accent: 'from-cyan-500 to-sky-500',
    ring: 'border-cyan-500/30',
    badge: 'Fast launch',
    includes: [
      'Core misinformation monitoring across major social and news sources',
      'Shared analyst workspace with configurable dashboards and alert queues',
      'Narrative clustering, source tracing, and evidence snapshots',
      'Standard onboarding for one mission, brand, or issue-monitoring lane',
    ],
  },
  {
    tier: 'Command Suite',
    audience: 'For government, newsroom, and national response operations',
    summary: 'Adds deeper OSINT workflows, response coordination, and broader watch coverage for organizations running daily high-stakes monitoring.',
    accent: 'from-violet-500 to-fuchsia-500',
    ring: 'border-violet-500/30',
    badge: 'Most requested',
    includes: [
      'Everything in Response Core plus six-suite OSINT enrichment',
      'Regional watchlists, escalation rules, and executive reporting views',
      'Deepfake/media forensics, risk-scoring controls, and narrative timelines',
      'Priority onboarding with role-based workflow configuration',
    ],
  },
  {
    tier: 'Fusion Enterprise',
    audience: 'For multi-team deployments and complex intelligence programs',
    summary: 'Designed for organizations that need tailored coverage, advanced integrations, and large-scale coordination across multiple operating teams.',
    accent: 'from-amber-500 to-rose-500',
    ring: 'border-amber-500/30',
    badge: 'Custom deployment',
    includes: [
      'Everything in Command Suite plus custom collectors and integration support',
      'Cross-team governance, admin controls, and deployment-specific workflows',
      'Expanded strategic reporting for leadership, risk, and external stakeholders',
      'Dedicated solution design for geography, sector, and mission requirements',
    ],
  },
];

const AUDIENCE_FOCUS = {
  government: {
    title: 'For government and public-interest response teams',
    body: 'Emphasis is typically on multilingual watch coverage, regional escalation, public-safety monitoring, and incident coordination across multiple stakeholders.',
    checklist: ['Regional monitoring rules', 'Escalation ownership clarity', 'High-signal briefing outputs'],
  },
  enterprise: {
    title: 'For enterprise risk, brand, and executive protection teams',
    body: 'Commercial scope usually centers on reputational risk, executive exposure, sector-specific watchlists, and leadership-ready reporting for fast decision cycles.',
    checklist: ['Brand and executive monitoring', 'Incident workflow fit', 'Cross-functional reporting'],
  },
  media: {
    title: 'For newsrooms and verification desks',
    body: 'Teams often prioritize source verification speed, false-claim triage, evidence packaging, and real-time context for editorial and standards decisions.',
    checklist: ['Verification speed', 'Evidence traceability', 'Editorial handoff support'],
  },
} as const;

export default function Landing({ setPage }: { setPage: (page: any) => void }) {
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<FeatureItem | null>(null);
  const [selectedOsint, setSelectedOsint] = useState<OsintItem | null>(null);
  const [selectedLearnResource, setSelectedLearnResource] = useState<LearnResource | null>(null);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '', company: '', orgType: '', role: '', country: '', state: '', city: '' });
  const [pricingForm, setPricingForm] = useState({ name: '', email: '', company: '', teamType: 'government', seats: '', priorities: '' });
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [demoInquiryId, setDemoInquiryId] = useState<number | null>(null);
  const [pricingSubmitted, setPricingSubmitted] = useState(false);
  const [pricingSubmitting, setPricingSubmitting] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [pricingInquiryId, setPricingInquiryId] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('demo');
  const [audienceFocus, setAudienceFocus] = useState<keyof typeof AUDIENCE_FOCUS>('government');
  const carouselTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Scroll-aware navbar — solidify + shrink after the hero scrolls past.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active-section tracking — highlights the current nav item as you scroll.
  useEffect(() => {
    const ids = NAV_LINKS.map(l => l.id);
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: [0, 0.25, 0.5, 1] }
    );
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Auto-advance carousel every 3.5s
  useEffect(() => {
    carouselTimer.current = setInterval(() => {
      setCarouselIdx(i => (i + 1) % CAROUSEL_FEATURES.length);
    }, 3500);
    return () => { if (carouselTimer.current) clearInterval(carouselTimer.current); };
  }, []);

  const goCarousel = (idx: number) => {
    if (carouselTimer.current) clearInterval(carouselTimer.current);
    setCarouselIdx(idx);
    carouselTimer.current = setInterval(() => {
      setCarouselIdx(i => (i + 1) % CAROUSEL_FEATURES.length);
    }, 3500);
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDemoSubmitting(true);
    setDemoError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/public/inquiries/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: demoForm.name,
          email: demoForm.email,
          company: demoForm.company,
          phone: demoForm.phone || null,
          org_type: demoForm.orgType,
          role: demoForm.role || null,
          country: demoForm.country,
          state: demoForm.state,
          city: demoForm.city || null,
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || errorPayload?.message || 'Unable to submit demo request.');
      }

      const result = await response.json();
      setDemoInquiryId(result.inquiry_id || null);
      setDemoSubmitted(true);
    } catch (error: any) {
      setDemoError(error?.message || 'Unable to submit demo request.');
    } finally {
      setDemoSubmitting(false);
    }
  };

  const handlePricingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPricingSubmitting(true);
    setPricingError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/public/inquiries/pricing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: pricingForm.name,
          email: pricingForm.email,
          company: pricingForm.company,
          team_type: pricingForm.teamType,
          expected_seats: pricingForm.seats || null,
          priorities: pricingForm.priorities,
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || errorPayload?.message || 'Unable to submit pricing request.');
      }

      const result = await response.json();
      setPricingInquiryId(result.inquiry_id || null);
      setPricingSubmitted(true);
    } catch (error: any) {
      setPricingError(error?.message || 'Unable to submit pricing request.');
    } finally {
      setPricingSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white font-sans overflow-x-hidden selection:bg-cyan-500/30">
      {/* Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-zinc-950/85 backdrop-blur-2xl border-b border-zinc-800/60 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.8)]'
          : 'bg-gradient-to-b from-zinc-950/70 to-transparent backdrop-blur-md border-b border-transparent'
      }`}>
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent" />

        <div className={`max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1700px] mx-auto px-4 md:px-6 flex items-center justify-between transition-all duration-500 ${
          scrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'
        }`}>

          {/* Logo */}
          <div className="flex items-center gap-2.5 relative group cursor-pointer shrink-0" onClick={() => setPage('landing')}>
            <div className="absolute inset-0 bg-cyan-500/15 blur-xl rounded-full group-hover:bg-cyan-400/25 transition-all duration-500" />
            <div className="p-2 md:p-2.5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl relative shadow-[0_0_18px_rgba(6,182,212,0.45)] group-hover:shadow-[0_0_28px_rgba(6,182,212,0.7)] transition-all duration-300">
              <ShieldAlert className="w-5 h-5 text-white" />
            </div>
            <div className="relative flex items-baseline gap-0">
              <span className="text-lg md:text-xl font-extrabold tracking-tight text-white">Crisis</span>
              <span className="text-lg md:text-xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Shield</span>
              <div className="absolute -bottom-0.5 left-0 right-0 h-px bg-gradient-to-r from-cyan-500/60 via-blue-400/40 to-transparent" />
            </div>
          </div>

          {/* Desktop Nav — glass pill with sliding active indicator */}
          <div className="hidden md:flex items-center gap-0.5 lg:gap-1 p-1 rounded-full bg-white/[0.03] border border-white/[0.06] backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            {NAV_LINKS.map(({ href, id, label, icon: Icon }) => {
              const isActive = activeSection === id;
              return (
                <a key={label} href={href}
                  className={`relative px-3 lg:px-4 py-1.5 text-sm font-semibold rounded-full transition-colors duration-200 ${
                    isActive ? 'text-white' : 'text-zinc-400 hover:text-white'
                  }`}>
                  {isActive && (
                    <motion.span layoutId="nav-active-pill"
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-600/20 border border-cyan-400/30 shadow-[0_0_18px_rgba(6,182,212,0.25)]"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                  )}
                  <span className="relative z-10 flex items-center gap-1.5">
                    {Icon && <Icon className="w-3 h-3 lg:w-3.5 lg:h-3.5 opacity-80" />}
                    {label}
                  </span>
                </a>
              );
            })}
          </div>

          {/* Right: Live badge + CTA */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* Live indicator */}
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              <span className="text-xs font-bold tracking-widest">LIVE</span>
            </div>

            <button onClick={() => setPage('login')}
              className="hidden sm:flex items-center gap-1.5 px-3 md:px-4 py-2 text-sm font-bold text-zinc-300 hover:text-white border border-zinc-700/60 hover:border-cyan-500/40 rounded-lg transition-all duration-200 hover:bg-cyan-500/5 hover:shadow-[0_0_12px_rgba(6,182,212,0.15)]">
              <Lock className="w-3.5 h-3.5" /> Sign In
            </button>

            <button onClick={() => setPage('signup')}
              className="group relative hidden sm:flex items-center gap-1.5 px-4 md:px-5 py-2 md:py-2.5 text-sm font-bold
                bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg overflow-hidden
                hover:from-cyan-400 hover:to-blue-500 transition-all duration-200
                shadow-[0_0_20px_rgba(6,182,212,0.35)] hover:shadow-[0_0_32px_rgba(6,182,212,0.55)]">
              {/* sheen sweep */}
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              <span className="relative z-10 flex items-center gap-1.5">Get Started <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
            </button>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-white/5">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden border-t border-zinc-800/50 bg-zinc-950/95 backdrop-blur-xl"
            >
              <div className="px-5 py-5 flex flex-col gap-1">
                {NAV_LINKS.map(({ href, id, label, icon: Icon }) => {
                  const isActive = activeSection === id;
                  return (
                    <a key={label} href={href} onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        isActive
                          ? 'text-white bg-gradient-to-r from-cyan-500/15 to-blue-600/15 border border-cyan-400/25'
                          : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      }`}>
                      {Icon && <Icon className="w-3.5 h-3.5 opacity-70" />}
                      {label}
                      {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-400" />}
                    </a>
                  );
                })}
                <div className="flex items-center gap-2 pt-3 mt-1 border-t border-zinc-800/60">
                  <button onClick={() => { setPage('login'); setMobileMenuOpen(false); }}
                    className="flex-1 py-2.5 text-sm font-bold text-zinc-300 hover:text-white transition-colors border border-zinc-700/60 hover:border-cyan-500/40 rounded-lg hover:bg-cyan-500/5">
                    Sign In
                  </button>
                  <button onClick={() => { setPage('signup'); setMobileMenuOpen(false); }}
                    className="flex-1 py-2.5 text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition-all shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                    Get Started
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-12 px-4 sm:pt-28 md:pt-32 lg:pt-28 md:pb-16 md:px-6 lg:min-h-[88vh] flex flex-col items-center justify-start">
        {/* Background glows — cyan/blue theme, responsive */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[400px] md:w-[900px] md:h-[700px] bg-cyan-500/6 blur-[180px] rounded-full pointer-events-none" />
        <div className="absolute top-1/3 right-0 w-[250px] h-[250px] md:w-[500px] md:h-[500px] bg-blue-600/5 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-10 left-0 w-[300px] h-[300px] md:w-[450px] md:h-[450px] bg-indigo-600/4 blur-[180px] rounded-full pointer-events-none" />

        {/* ═══ STARTUP INDIA (DPIIT) RECOGNITION — top right corner ═══ */}
        <motion.div
          initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="absolute top-20 right-3 sm:top-24 sm:right-6 md:top-24 lg:top-24 z-20
            inline-flex items-center gap-2.5 sm:gap-3.5
            pl-2.5 pr-3 py-2 sm:pl-3 sm:pr-4 sm:py-2.5
            rounded-2xl bg-white border border-white/40
            shadow-[0_10px_40px_-8px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.05)]
            ring-1 ring-emerald-500/10">
          <img src="/assets/startupindia.jpeg" alt="Startup India — DPIIT recognized"
            className="h-11 sm:h-14 w-auto object-contain select-none shrink-0" draggable={false} />
          <div className="hidden sm:block leading-snug border-l border-zinc-200 pl-3.5">
            <p className="text-[13px] font-extrabold tracking-wide text-zinc-900 uppercase">Recognized Startup</p>
            <p className="text-[12px] font-semibold text-zinc-700">DPIIT &middot; <span className="text-emerald-600">Startup India</span></p>
            <p className="text-[11px] font-medium text-zinc-500">Government of India</p>
          </div>
        </motion.div>

        {/* ═══ CENTERED GLOSSY TAGLINE — full width, top of hero ═══ */}
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="relative z-10 text-center w-full mt-10 sm:mt-10 lg:mt-16 mb-8 md:mb-12 lg:mb-14 px-4"
        >
          <div className="inline-flex flex-wrap items-center justify-center gap-2 md:gap-4
            px-5 py-3 md:px-8 md:py-4
            rounded-full
            bg-gradient-to-r from-cyan-950/80 via-blue-950/90 to-cyan-950/80
            border border-cyan-400/25
            backdrop-blur-md
            shadow-[0_0_50px_rgba(6,182,212,0.2),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-1px_0_rgba(6,182,212,0.1)]">
            <span className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl
              font-extrabold tracking-widest uppercase
              text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-blue-100 to-cyan-200">
              Real-Time AI-Powered OSINT Platform
            </span>
            <span className="hidden sm:block w-px h-5 md:h-6 bg-cyan-500/40 rounded-full" />
            <div className="flex items-center gap-2 md:gap-3">
              <span className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-cyan-300 tracking-wider drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]">Detect</span>
              <span className="text-cyan-500/50 text-base md:text-lg select-none">•</span>
              <span className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-blue-300 tracking-wider drop-shadow-[0_0_8px_rgba(96,165,250,0.6)]">Predict</span>
              <span className="text-cyan-500/50 text-base md:text-lg select-none">•</span>
              <span className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-emerald-300 tracking-wider drop-shadow-[0_0_8px_rgba(52,211,153,0.6)]">Respond</span>
            </div>
          </div>
        </motion.div>

        {/* ═══ TWO-COLUMN CONTENT ═══ */}
        <div className="max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1700px] mx-auto w-full grid lg:grid-cols-2 gap-8 md:gap-12 xl:gap-16 2xl:gap-24 items-center relative z-10">

          {/* Left: copy */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs sm:text-xs font-bold tracking-widest mb-4 md:mb-6 uppercase">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-cyan-400 animate-pulse" /> CrisisShield Intelligence Private Limited
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-extrabold leading-[1.08] tracking-tight mb-5 md:mb-8">
              Detect global{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-red-500">crises</span>{' '}
              before they unfold.
            </h1>

            <p className="text-sm sm:text-base md:text-lg xl:text-xl 2xl:text-2xl text-zinc-400 font-light mb-8 md:mb-10 leading-relaxed max-w-xl 2xl:max-w-2xl">
              Six specialized intelligence suites — cyber threat intel, identity recon, media forensics, compliance screening, geo-transport tracking, and neural AI forensics — deliver government-grade actionable intelligence at scale.
            </p>

            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <button onClick={() => setPage('signup')}
                className="px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm md:text-base font-bold rounded-xl
                  hover:from-cyan-400 hover:to-blue-500 transition-all duration-200 flex items-center gap-2
                  shadow-[0_0_28px_rgba(6,182,212,0.4)] hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] group">
                Get Started <ChevronRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => setPage('login')}
                className="px-6 py-3 sm:px-8 sm:py-4 bg-transparent border border-cyan-500/30 hover:border-cyan-400/60 text-white text-sm md:text-base font-bold rounded-xl transition-all duration-200 hover:bg-cyan-500/8 hover:shadow-[0_0_16px_rgba(6,182,212,0.15)]">
                Government Login
              </button>
            </div>
          </motion.div>

          {/* Right: animated CrisisShield logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative flex items-center justify-center mt-6 lg:mt-0"
          >
            {/* Scale wrapper — rings stay at fixed px, whole block scales per viewport */}
            <div className="relative flex items-center justify-center
              scale-[0.58] xs:scale-[0.65] sm:scale-75 md:scale-90 lg:scale-100 xl:scale-110 2xl:scale-125
              -my-24 xs:-my-20 sm:-my-14 md:-my-6 lg:my-0 xl:my-6 2xl:my-14"
              style={{ width: 460, height: 460 }}>

              {/* ── Outer ring (slow CW) ── */}
              <div className="absolute w-[440px] h-[440px] rounded-full border border-cyan-400/20 animate-spin" style={{ animationDuration: '22s' }}>
                {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                  <div key={i}
                    className="absolute w-2.5 h-2.5 rounded-full bg-cyan-400/70"
                    style={{ boxShadow: '0 0 8px 2px rgba(34,211,238,0.7)', top: '50%', left: '50%', transform: `rotate(${deg}deg) translateX(215px) translateY(-50%)` }} />
                ))}
              </div>

              {/* ── Middle ring (CCW, dashed) ── */}
              <div className="absolute w-[360px] h-[360px] rounded-full border border-dashed border-blue-500/25 animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}>
                {[30, 90, 150, 210, 270, 330].map((deg, i) => (
                  <div key={i}
                    className="absolute w-2 h-2 rounded-full bg-blue-400/60"
                    style={{ boxShadow: '0 0 6px 1px rgba(96,165,250,0.7)', top: '50%', left: '50%', transform: `rotate(${deg}deg) translateX(177px) translateY(-50%)` }} />
                ))}
              </div>

              {/* ── Inner ring (CW, fast) ── */}
              <div className="absolute w-[280px] h-[280px] rounded-full border border-cyan-300/15 animate-spin" style={{ animationDuration: '8s' }}>
                {[0, 90, 180, 270].map((deg, i) => (
                  <div key={i}
                    className="absolute w-1.5 h-1.5 rounded-full bg-cyan-200/80"
                    style={{ boxShadow: '0 0 5px 1px rgba(165,243,252,0.9)', top: '50%', left: '50%', transform: `rotate(${deg}deg) translateX(138px) translateY(-50%)` }} />
                ))}
              </div>

              {/* ── Glow layers ── */}
              <div className="absolute w-56 h-56 bg-cyan-500/12 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '3s' }} />
              <div className="absolute w-72 h-72 bg-blue-700/8 rounded-full blur-[60px] animate-pulse" style={{ animationDuration: '5s' }} />

              {/* ── Floating data-stream particles ── */}
              {[
                { angle: 15, dist: 195, size: 1.5, dur: '2.4s', delay: '0s' },
                { angle: 75, dist: 185, size: 1, dur: '3.1s', delay: '0.8s' },
                { angle: 140, dist: 200, size: 2, dur: '2.7s', delay: '0.3s' },
                { angle: 200, dist: 190, size: 1, dur: '3.5s', delay: '1.2s' },
                { angle: 265, dist: 195, size: 1.5, dur: '2.9s', delay: '0.5s' },
                { angle: 320, dist: 185, size: 1, dur: '2.2s', delay: '1.5s' },
              ].map(({ angle, dist, size, dur, delay }, i) => {
                const rad = (angle * Math.PI) / 180;
                const x = Math.cos(rad) * dist;
                const y = Math.sin(rad) * dist;
                return (
                  <motion.div key={i}
                    className="absolute rounded-full bg-cyan-300"
                    style={{ width: size * 4, height: size * 4, left: '50%', top: '50%', marginLeft: x, marginTop: y, boxShadow: `0 0 ${size * 4}px rgba(34,211,238,0.9)` }}
                    animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.4, 0.8] }}
                    transition={{ duration: parseFloat(dur), delay: parseFloat(delay), repeat: Infinity, ease: 'easeInOut' }} />
                );
              })}

              {/* ── Company logo (round, rotating emblem) ── */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="relative z-10"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
                  className="w-[300px] h-[300px] rounded-full overflow-hidden border-2 border-red-500/40
                    shadow-[0_0_60px_rgba(239,68,68,0.45),inset_0_0_40px_rgba(0,0,0,0.6)]"
                  style={{ filter: 'drop-shadow(0 0 30px rgba(239,68,68,0.4))' }}
                >
                  <img
                    src="/assets/crisisshield-logo.png"
                    alt="CrisisShield Intelligence Private Limited"
                    className="w-full h-full object-cover select-none pointer-events-none scale-125"
                    style={{ objectPosition: '50% 36%' }}
                    draggable={false}
                  />
                </motion.div>
              </motion.div>

              {/* Sweeping scan overlay */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full opacity-25">
                <motion.div
                  className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-300 to-transparent"
                  animate={{ top: ['0%', '100%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
              </div>

            </div>{/* end scale wrapper */}
          </motion.div>
        </div>
      </section>

      {/* Live Stats Ticker */}
      <section className="relative py-6 border-y border-zinc-800/50 bg-zinc-900/30 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '2.4M+', label: 'Posts Analyzed Daily', icon: BarChart3 },
              { value: '< 200ms', label: 'Threat Detection Latency', icon: Zap },
              { value: '97.3%', label: 'Classification Accuracy', icon: Target },
              { value: '24/7', label: 'Real-Time Monitoring', icon: Clock },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                  <stat.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-extrabold text-white">{stat.value}</p>
                  <p className="text-xs text-zinc-500 font-medium">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEMO SECTION ── */}
      <section className="relative py-28 px-6" id="demo">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-cyan-500/8 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-6xl mx-auto relative z-10">

          {/* Section header */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}
            className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold tracking-wider mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" /> PLATFORM CAPABILITIES
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-5">
              See it in <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-cyan-400">action</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Five intelligence layers working in concert — no internal architecture revealed.
              <span className="text-zinc-500"> Full live demo available on request.</span>
            </p>
          </motion.div>

          {/* Carousel */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>

            {/* Animated border frame */}
            <div className="relative">
              <div className="absolute -inset-[2px] rounded-2xl opacity-60"
                style={{ background: `linear-gradient(135deg, ${CAROUSEL_FEATURES[carouselIdx].glow.replace('0.22', '0.8')}, transparent, ${CAROUSEL_FEATURES[carouselIdx].glow.replace('0.22', '0.5')})`, transition: 'all 0.6s ease' }} />

              <div className="relative bg-zinc-900/80 backdrop-blur-xl rounded-2xl overflow-hidden border border-zinc-800/60"
                style={{ minHeight: 460 }}>

                {/* Animated scan line */}
                <div className="absolute left-0 right-0 h-px pointer-events-none z-20"
                  style={{ background: `linear-gradient(to right, transparent, ${CAROUSEL_FEATURES[carouselIdx].glow.replace('0.22', '0.7')}, transparent)` }}>
                  <motion.div className="absolute left-0 right-0 h-px"
                    animate={{ top: ['0%', '100%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
                </div>
                <motion.div className="absolute left-0 right-0 h-[1.5px] z-20 pointer-events-none"
                  style={{ background: `linear-gradient(to right, transparent, ${CAROUSEL_FEATURES[carouselIdx].glow.replace('0.22', '0.6')}, transparent)` }}
                  animate={{ top: ['5%', '98%', '5%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />

                <AnimatePresence mode="wait">
                  <motion.div key={carouselIdx}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    className="grid md:grid-cols-2 gap-0 h-full">

                    {/* LEFT — feature info */}
                    <div className="p-8 md:p-10 flex flex-col justify-between border-b md:border-b-0 md:border-r border-zinc-800/60">
                      <div>
                        {/* Tag + icon */}
                        <div className="flex items-center gap-3 mb-5">
                          <div className={`p-2.5 rounded-xl bg-gradient-to-br ${CAROUSEL_FEATURES[carouselIdx].gradient} bg-opacity-20 shadow-lg`}
                            style={{ boxShadow: `0 0 20px ${CAROUSEL_FEATURES[carouselIdx].glow}` }}>
                            {React.createElement(CAROUSEL_FEATURES[carouselIdx].icon, { className: 'w-5 h-5 text-white' })}
                          </div>
                          <span className="text-xs font-bold tracking-widest text-zinc-500 uppercase">{CAROUSEL_FEATURES[carouselIdx].tag}</span>
                          {(CAROUSEL_FEATURES[carouselIdx] as typeof CAROUSEL_FEATURES[0] & { comingSoon?: boolean }).comingSoon && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-black tracking-widest uppercase border border-amber-500/50 bg-amber-500/10 text-amber-400">Coming Soon</span>
                          )}
                        </div>

                        <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">
                          {CAROUSEL_FEATURES[carouselIdx].title}
                        </h3>
                        <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-7">
                          {CAROUSEL_FEATURES[carouselIdx].desc}
                        </p>

                        {/* Metric pills */}
                        <div className="flex flex-wrap gap-3 mb-7">
                          {CAROUSEL_FEATURES[carouselIdx].metrics.map((m, i) => (
                            <div key={i} className="px-3 py-2 rounded-xl bg-zinc-800/80 border border-zinc-700/50">
                              <div className={`text-xl font-extrabold ${m.color}`}>{m.value}</div>
                              <div className="text-xs text-zinc-500 font-medium mt-0.5">{m.label}</div>
                            </div>
                          ))}
                        </div>

                        {/* Alert chip */}
                        <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold ${CAROUSEL_FEATURES[carouselIdx].alert.color}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                          <span className="font-bold opacity-70">{CAROUSEL_FEATURES[carouselIdx].alert.level}</span>
                          <span className="opacity-80">·</span>
                          <span>{CAROUSEL_FEATURES[carouselIdx].alert.label}</span>
                        </div>
                      </div>

                      {/* Request demo CTA */}
                      <button onClick={() => setDemoModalOpen(true)}
                        className={`mt-8 w-full py-3.5 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 bg-gradient-to-r ${CAROUSEL_FEATURES[carouselIdx].gradient} hover:opacity-90 transition-all shadow-lg group`}
                        style={{ boxShadow: `0 0 24px ${CAROUSEL_FEATURES[carouselIdx].glow}` }}>
                        Request Full Demo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>

                    {/* RIGHT — animated bars + live feed */}
                    <div className="p-8 md:p-10 flex flex-col justify-between">
                      {/* Capability bars */}
                      <div className="space-y-5 mb-8">
                        <motion.div
                          key={`cap-label-${carouselIdx}`}
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4 }}
                          className="flex items-center gap-2 mb-4"
                        >
                          <span className={`inline-block w-1.5 h-3.5 rounded-sm bg-gradient-to-b ${CAROUSEL_FEATURES[carouselIdx].gradient}`}
                            style={{ boxShadow: `0 0 6px ${CAROUSEL_FEATURES[carouselIdx].glow}` }} />
                          <span className={`text-xs font-extrabold tracking-[0.2em] uppercase bg-gradient-to-r ${CAROUSEL_FEATURES[carouselIdx].gradient} bg-clip-text text-transparent`}>
                            Capability Metrics
                          </span>
                          <motion.span
                            animate={{ opacity: [0.4, 1, 0.4] }}
                            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                            className="ml-auto text-xs font-bold tracking-widest text-zinc-500 uppercase"
                          >
                            Live
                          </motion.span>
                        </motion.div>
                        {CAROUSEL_FEATURES[carouselIdx].bars.map((b, i) => (
                          <div key={i}>
                            <div className="flex justify-between text-xs mb-1.5">
                              <span className="text-zinc-400 font-medium">{b.label}</span>
                              <span className="text-zinc-300 font-bold">{b.pct}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-zinc-800">
                              <motion.div
                                className={`h-2 rounded-full bg-gradient-to-r ${b.color}`}
                                initial={{ width: 0 }}
                                animate={{ width: `${b.pct}%` }}
                                transition={{ duration: 0.8, delay: i * 0.15, ease: 'easeOut' }}
                                style={{ boxShadow: `0 0 8px ${CAROUSEL_FEATURES[carouselIdx].glow}` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Fake live activity feed */}
                      <div className="rounded-xl bg-zinc-950/70 border border-zinc-800/60 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold tracking-widest text-zinc-400 uppercase">Live Activity</span>
                          <span className="flex items-center gap-1 text-xs text-emerald-400 font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
                          </span>
                        </div>
                        <div className="space-y-2">
                          {[
                            { time: '0s ago', msg: 'Threat classified', color: 'text-cyan-400' },
                            { time: '1.2s ago', msg: 'Network node mapped', color: 'text-blue-400' },
                            { time: '3.1s ago', msg: 'Alert escalated', color: 'text-amber-400' },
                            { time: '5.4s ago', msg: 'Evidence hashed', color: 'text-violet-400' },
                          ].map((item, i) => (
                            <motion.div key={`${carouselIdx}-${i}`}
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.3 + i * 0.1 }}
                              className="flex items-center justify-between text-xs">
                              <span className={`font-medium ${item.color}`}>{item.msg}</span>
                              <span className="text-zinc-400">{item.time}</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      {/* Slide progress dots */}
                      <div className="flex items-center justify-center gap-2 mt-6">
                        <button onClick={() => goCarousel((carouselIdx - 1 + CAROUSEL_FEATURES.length) % CAROUSEL_FEATURES.length)}
                          className="p-1 text-zinc-400 hover:text-zinc-300 transition-colors">
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        {CAROUSEL_FEATURES.map((_, i) => (
                          <button key={i} onClick={() => goCarousel(i)}
                            className={`rounded-full transition-all duration-300 ${
                              i === carouselIdx ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-zinc-700 hover:bg-zinc-500'
                            }`} />
                        ))}
                        <button onClick={() => goCarousel((carouselIdx + 1) % CAROUSEL_FEATURES.length)}
                          className="p-1 text-zinc-400 hover:text-zinc-300 transition-colors">
                          <ChevronRightIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Bottom label */}
            <p className="text-center text-xs text-zinc-400 mt-5 tracking-wide">
              Showing {carouselIdx + 1} of {CAROUSEL_FEATURES.length} capabilities · Full demo on request
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── REQUEST DEMO MODAL ── */}
      <AnimatePresence>
        {demoModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            onClick={() => { setDemoModalOpen(false); setDemoSubmitted(false); setDemoError(null); setDemoInquiryId(null); }}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

            <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl"
              onClick={e => e.stopPropagation()}>

              {/* Top gradient bar */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cyan-500 via-violet-500 to-rose-500" />

              {/* Close */}
              <button onClick={() => { setDemoModalOpen(false); setDemoSubmitted(false); setDemoError(null); setDemoInquiryId(null); }}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-all">
                <X className="w-5 h-5" />
              </button>

              <div className="p-8">
                {!demoSubmitted ? (
                  <>
                    <div className="mb-6">
                      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-widest mb-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> FULL DEMO REQUEST
                      </div>
                      <h3 className="text-2xl font-extrabold text-white mb-2">See the complete platform</h3>
                      <p className="text-zinc-400 text-sm">We'll schedule a personalized walkthrough of all intelligence modules with live data.</p>
                    </div>

                    <form onSubmit={handleDemoSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <input required value={demoForm.name}
                              onChange={e => setDemoForm(f => ({ ...f, name: e.target.value }))}
                              placeholder="Abdinav Singh"
                              className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all" />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Work Email *</label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <input required type="email" value={demoForm.email}
                              onChange={e => setDemoForm(f => ({ ...f, email: e.target.value }))}
                              placeholder="you@org.com"
                              className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                          <input type="tel" value={demoForm.phone}
                            onChange={e => setDemoForm(f => ({ ...f, phone: e.target.value }))}
                            placeholder="+91 98765 43210"
                            className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all" />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Organization *</label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                          <input required value={demoForm.company}
                            onChange={e => setDemoForm(f => ({ ...f, company: e.target.value }))}
                            placeholder="Ministry / Agency / Company"
                            className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Organization Type *</label>
                          <div className="relative">
                            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                            <select required value={demoForm.orgType}
                              onChange={e => setDemoForm(f => ({ ...f, orgType: e.target.value }))}
                              className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all appearance-none">
                              <option value="">Select type...</option>
                              <option>Government Agency</option>
                              <option>Defense / Military</option>
                              <option>Law Enforcement</option>
                              <option>Intelligence / Security</option>
                              <option>News / Media House</option>
                              <option>Enterprise / Corporate</option>
                              <option>NGO / Non-Profit</option>
                              <option>Research / Academia</option>
                              <option>Startup</option>
                              <option>Investor / VC</option>
                              <option>Other</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Your Role</label>
                          <select value={demoForm.role}
                            onChange={e => setDemoForm(f => ({ ...f, role: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all appearance-none">
                            <option value="">Select role...</option>
                            <option>Founder / C-Suite</option>
                            <option>Director / VP</option>
                            <option>Manager / Lead</option>
                            <option>Analyst / Researcher</option>
                            <option>Engineer / Technical</option>
                            <option>Officer / Field Staff</option>
                            <option>Other</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Country *</label>
                          <div className="relative">
                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                            <select required value={demoForm.country}
                              onChange={e => setDemoForm(f => ({ ...f, country: e.target.value }))}
                              className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all appearance-none">
                              <option value="">Select country...</option>
                              {DEMO_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">State / Province *</label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <input required value={demoForm.state}
                              onChange={e => setDemoForm(f => ({ ...f, state: e.target.value }))}
                              placeholder="e.g. Maharashtra"
                              className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">City</label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                          <input value={demoForm.city}
                            onChange={e => setDemoForm(f => ({ ...f, city: e.target.value }))}
                            placeholder="e.g. Mumbai"
                            className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-all" />
                        </div>
                      </div>

                      {demoError && <p className="text-sm text-rose-400">{demoError}</p>}

                      <button type="submit"
                        disabled={demoSubmitting}
                        className="w-full py-3.5 mt-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-[0_0_24px_rgba(6,182,212,0.35)] hover:shadow-[0_0_36px_rgba(6,182,212,0.5)] transition-all group disabled:opacity-60">
                        <Send className="w-4 h-4" /> {demoSubmitting ? 'Submitting request…' : 'Request Demo'} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </form>

                    <p className="text-center text-xs text-zinc-400 mt-4">We respond within 24 hours · No spam · NDA available</p>
                  </>
                ) : (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                      <Check className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-2xl font-extrabold text-white mb-3">Request received!</h3>
                    <p className="text-zinc-400 text-sm max-w-sm mx-auto leading-relaxed">
                      Thank you, <span className="text-white font-semibold">{demoForm.name}</span>. Our team will reach out to <span className="text-cyan-400">{demoForm.email}</span> within 24 hours to schedule your personalized demo.
                    </p>
                    {demoInquiryId && <p className="text-xs text-zinc-500 mt-4">Reference ID: #{demoInquiryId}</p>}
                    <button onClick={() => { setDemoModalOpen(false); setDemoSubmitted(false); setDemoError(null); setDemoInquiryId(null); }}
                      className="mt-8 px-8 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl transition-all text-sm">
                      Close
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedLearnResource && (() => {
          const LearnIcon = selectedLearnResource.icon;
          return (
            <motion.div
              key="learn-modal-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-md"
              onClick={() => setSelectedLearnResource(null)}>
              <motion.div
                key="learn-modal"
                initial={{ opacity: 0, scale: 0.94, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 18 }}
                transition={{ duration: 0.25 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-2xl rounded-3xl border border-zinc-800/70 bg-zinc-950 shadow-2xl overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500" />
                <button onClick={() => setSelectedLearnResource(null)}
                  className="absolute top-4 right-4 p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors">
                  <X className="w-5 h-5" />
                </button>
                <div className="p-8 md:p-10">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 shrink-0">
                      <LearnIcon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-400">{selectedLearnResource.tag}</span>
                      <h3 className="text-2xl md:text-3xl font-extrabold text-white mt-2 mb-3">{selectedLearnResource.title}</h3>
                      <p className="text-zinc-400 leading-relaxed">{selectedLearnResource.desc}</p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-5 mb-6">
                    <p className="text-sm font-semibold text-emerald-200 leading-relaxed">{selectedLearnResource.highlight}</p>
                  </div>
                  <div className="space-y-3 mb-8">
                    {selectedLearnResource.bullets.map((bullet) => (
                      <div key={bullet} className="flex items-start gap-3 rounded-2xl border border-zinc-800/60 bg-zinc-900/50 p-4 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        <span>{bullet}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={() => { setSelectedLearnResource(null); setDemoModalOpen(true); }}
                      className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold hover:opacity-90 transition-opacity">
                      Request a guided walkthrough
                    </button>
                    <button onClick={() => setSelectedLearnResource(null)}
                      className="flex-1 py-3.5 rounded-xl border border-zinc-700 bg-zinc-900 text-zinc-200 font-bold hover:bg-zinc-800 transition-colors">
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      <AnimatePresence>
        {pricingModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            onClick={() => { setPricingModalOpen(false); setPricingSubmitted(false); setPricingError(null); setPricingInquiryId(null); }}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 18 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, y: 18 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-xl rounded-3xl border border-zinc-800 bg-zinc-950 shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}>
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-rose-500 to-cyan-500" />
              <button onClick={() => { setPricingModalOpen(false); setPricingSubmitted(false); setPricingError(null); setPricingInquiryId(null); }}
                className="absolute top-4 right-4 p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors">
                <X className="w-5 h-5" />
              </button>
              <div className="p-8 md:p-10">
                {!pricingSubmitted ? (
                  <>
                    <div className="mb-6">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold tracking-widest mb-4">
                        <Briefcase className="w-3.5 h-3.5" /> PRICING REQUEST
                      </div>
                      <h3 className="text-2xl font-extrabold text-white mb-2">Start a pricing conversation</h3>
                      <p className="text-sm text-zinc-400 leading-relaxed">Share your team type, rough seat range, and the outcomes you care about. We can scope a fit without forcing a generic package.</p>
                    </div>

                    <form onSubmit={handlePricingSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
                          <input required value={pricingForm.name}
                            onChange={e => setPricingForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60"
                            placeholder="Your name" />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Work Email *</label>
                          <input required type="email" value={pricingForm.email}
                            onChange={e => setPricingForm(f => ({ ...f, email: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60"
                            placeholder="you@organization.com" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Organization *</label>
                        <input required value={pricingForm.company}
                          onChange={e => setPricingForm(f => ({ ...f, company: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60"
                          placeholder="Agency, newsroom, company, or team name" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Team Type *</label>
                          <select value={pricingForm.teamType}
                            onChange={e => setPricingForm(f => ({ ...f, teamType: e.target.value as keyof typeof AUDIENCE_FOCUS }))}
                            className="w-full px-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60 appearance-none">
                            <option value="government">Government / Public Sector</option>
                            <option value="enterprise">Enterprise / Risk / Brand</option>
                            <option value="media">Media / Verification Desk</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Expected Seats</label>
                          <input value={pricingForm.seats}
                            onChange={e => setPricingForm(f => ({ ...f, seats: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60"
                            placeholder="e.g. 5-10 analysts" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">Priorities *</label>
                        <textarea required rows={4} value={pricingForm.priorities}
                          onChange={e => setPricingForm(f => ({ ...f, priorities: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-zinc-800/80 border border-zinc-700/60 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500/60 resize-none"
                          placeholder="Describe what you need to monitor, which workflows matter, and whether you care most about response speed, verification, brand protection, regional coverage, or executive reporting." />
                      </div>
                      {pricingError && <p className="text-sm text-rose-400">{pricingError}</p>}
                      <button type="submit"
                        disabled={pricingSubmitting}
                        className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-rose-500 to-cyan-500 text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
                        {pricingSubmitting ? 'Submitting request…' : 'Request pricing review'}
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                      <Check className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-2xl font-extrabold text-white mb-3">Pricing request received</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed max-w-sm mx-auto">
                      Thanks, <span className="text-white font-semibold">{pricingForm.name}</span>. We will review your scope and follow up at <span className="text-cyan-400">{pricingForm.email}</span> with the right deployment conversation.
                    </p>
                    {pricingInquiryId && <p className="text-xs text-zinc-500 mt-4">Reference ID: #{pricingInquiryId}</p>}
                    <button onClick={() => { setPricingModalOpen(false); setPricingSubmitted(false); setPricingError(null); setPricingInquiryId(null); }}
                      className="mt-8 px-8 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold transition-colors">
                      Close
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Features Section */}
      <section className="relative py-16 px-4 md:py-32 md:px-6" id="features">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-500/5 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}
            className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-bold tracking-wider mb-6">
              <Zap className="w-3.5 h-3.5" /> CORE CAPABILITIES
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
              Intelligence at <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">every layer</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              A comprehensive platform combining core AI detection with a six-suite OSINT pipeline, brand & reputation protection, and competitive business intelligence — delivering threat detection, entity intelligence, market signals, and synthetic media forensics at enterprise scale.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
{FEATURES.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <motion.div key={i} variants={fadeUp} whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  onClick={() => setSelectedFeature(feat)}
                  className="group relative p-8 rounded-2xl border border-zinc-800/50 bg-zinc-900/30 hover:bg-zinc-900/60 backdrop-blur-sm transition-all duration-300 hover:border-zinc-700/50 hover:shadow-2xl hover:shadow-cyan-500/5 cursor-pointer">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl" />
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${feat.gradient} shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs font-bold tracking-wider text-zinc-500 bg-zinc-800/50 px-2 py-1 rounded-full">{feat.tag}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">{feat.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{feat.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-xs text-zinc-400 group-hover:text-cyan-500 transition-colors">
                    <ChevronRight className="w-3 h-3" /> <span>Click to explore</span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Feature Detail Modal */}
          <AnimatePresence>
            {selectedFeature && (() => {
              const ModalIcon = selectedFeature.icon;
              return (
                <motion.div
                  key="feature-modal-backdrop"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                  onClick={() => setSelectedFeature(null)}>
                  <motion.div
                    key="feature-modal"
                    initial={{ opacity: 0, scale: 0.92, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 24 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-2xl rounded-2xl border border-zinc-700/60 bg-zinc-950 shadow-2xl overflow-hidden">
                    {/* Gradient header bar */}
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${selectedFeature.gradient}`} />
                    {/* Header */}
                    <div className="p-8 pb-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`p-3.5 rounded-xl bg-gradient-to-br ${selectedFeature.gradient} shadow-lg shrink-0`}>
                            <ModalIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <span className="text-xs font-bold tracking-wider text-zinc-500 bg-zinc-800 px-2 py-1 rounded-full">{selectedFeature.tag}</span>
                            <h3 className="text-2xl font-extrabold text-white mt-2">{selectedFeature.title}</h3>
                          </div>
                        </div>
                        <button onClick={() => setSelectedFeature(null)}
                          className="shrink-0 p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors mt-1">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      {/* Highlight pill */}
                      <p className={`mt-5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-gradient-to-r ${selectedFeature.gradient} bg-opacity-10 text-white/90`}>
                        {selectedFeature.highlight}
                      </p>
                    </div>
                    {/* Body */}
                    <div className="p-8 pt-6 grid grid-cols-1 gap-6">
                      <div>
                        <p className="text-sm text-zinc-400 leading-relaxed mb-5">{selectedFeature.desc}</p>
                        <ul className="space-y-2.5">
                          {selectedFeature.details.map((d, idx) => (
                            <li key={idx} className="flex items-start gap-2.5 text-sm text-zinc-300">
                              <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {/* Stats row */}
                      <div className="grid grid-cols-3 gap-3">
                        {selectedFeature.stats.map((s, idx) => (
                          <div key={idx} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 text-center">
                            <div className={`text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${selectedFeature.gradient}`}>{s.value}</div>
                            <div className="text-[11px] text-zinc-500 mt-1 font-medium">{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </section>

      {/* OSINT Intelligence Suites */}
      <section className="relative py-16 px-4 md:py-32 md:px-6 bg-zinc-900/20" id="osint">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(6,182,212,0.04)_0%,_transparent_70%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}
            className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-wider mb-6">
              <FileSearch className="w-3.5 h-3.5" /> 6-SUITE OSINT FRAMEWORK
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
              Waterfall intelligence <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">pipeline</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Six specialized modules run in parallel against each extracted entity — cyber, identity, media, compliance, geo, and neural intelligence are gathered independently and merged into a unified threat picture from a single URL, username, or post.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {OSINT_SUITES.map((suite, i) => {
              const Icon = suite.icon;
              return (
                <motion.div key={i} variants={fadeUp} whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  onClick={() => setSelectedOsint(suite)}
                  className={`group relative p-8 rounded-2xl border ${suite.border} bg-zinc-900/30 hover:bg-zinc-900/60 backdrop-blur-sm transition-all duration-300 cursor-pointer`}>
                  <div className="flex items-start justify-between mb-5">
                    <motion.div
                      className={`p-3 rounded-xl bg-gradient-to-br ${suite.gradient} shadow-lg overflow-hidden`}
                      animate={([
                          { scale: [1, 1.2, 1] },
                          { rotate: [0, 360] },
                          { y: [0, -5, 0] },
                          { opacity: [1, 0.45, 1] },
                          { rotate: [-12, 12, -12] },
                          { scale: [1, 1.25, 0.92, 1] },
                        ] as any[])[i % 6]}
                      transition={{
                        repeat: Infinity,
                        duration: [2, 9, 3, 1.8, 2.2, 1.6][i % 6],
                        ease: i % 6 === 1 ? 'linear' : 'easeInOut',
                      }}>
                      <Icon className="w-5 h-5 text-white" />
                    </motion.div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs font-bold tracking-wider text-zinc-500 bg-zinc-800/50 px-2 py-1 rounded-full">{suite.tag}</span>
                      <span className="text-2xl font-extrabold text-zinc-700">{suite.num}</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors">{suite.suite}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed mb-4">{suite.desc}</p>
                  <ul className="space-y-2">
                    {suite.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2.5 text-sm text-zinc-400">
                        <span className="w-1 h-1 rounded-full bg-zinc-500 flex-shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex items-center gap-1 text-xs text-zinc-400 group-hover:text-cyan-500 transition-colors">
                    <ChevronRight className="w-3 h-3" /> <span>Click to explore</span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* OSINT Suite Detail Modal */}
          <AnimatePresence>
            {selectedOsint && (() => {
              const ModalIcon = selectedOsint.icon;
              return (
                <motion.div
                  key="osint-modal-backdrop"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                  onClick={() => setSelectedOsint(null)}>
                  <motion.div
                    key="osint-modal"
                    initial={{ opacity: 0, scale: 0.92, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 24 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-2xl rounded-2xl border border-zinc-700/60 bg-zinc-950 shadow-2xl overflow-hidden">
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${selectedOsint.gradient}`} />
                    <div className="p-8 pb-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`p-3.5 rounded-xl bg-gradient-to-br ${selectedOsint.gradient} shadow-lg shrink-0`}>
                            <ModalIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold tracking-wider text-zinc-500 bg-zinc-800 px-2 py-1 rounded-full">{selectedOsint.tag}</span>
                              <span className="text-2xl font-extrabold text-zinc-700">{selectedOsint.num}</span>
                            </div>
                            <h3 className="text-2xl font-extrabold text-white mt-2">{selectedOsint.suite}</h3>
                          </div>
                        </div>
                        <button onClick={() => setSelectedOsint(null)}
                          className="shrink-0 p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors mt-1">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <p className={`mt-5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-gradient-to-r ${selectedOsint.gradient} bg-opacity-10 text-white/90`}>
                        {selectedOsint.highlight}
                      </p>
                    </div>
                    <div className="p-8 pt-6 grid grid-cols-1 gap-6">
                      <div>
                        <p className="text-sm text-zinc-400 leading-relaxed mb-5">{selectedOsint.desc}</p>
                        <ul className="space-y-2.5">
                          {selectedOsint.details.map((d, idx) => (
                            <li key={idx} className="flex items-start gap-2.5 text-sm text-zinc-300">
                              <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {selectedOsint.stats.map((s, idx) => (
                          <div key={idx} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 text-center">
                            <div className={`text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${selectedOsint.gradient}`}>{s.value}</div>
                            <div className="text-[11px] text-zinc-500 mt-1 font-medium">{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </section>

      {/* Learn Section */}
      <section className="relative py-16 px-4 md:py-32 md:px-6 bg-zinc-900/20" id="learn">
        <div className="absolute top-0 left-1/3 w-[520px] h-[520px] bg-emerald-500/5 blur-[160px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] bg-cyan-500/5 blur-[140px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}
            className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold tracking-wider mb-6">
              <BookOpen className="w-3.5 h-3.5" /> LEARN CRISISSHIELD
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
              Build operator knowledge, not just <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">tool familiarity</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-3xl mx-auto">
              The Learn space is designed for analysts, communications teams, and decision-makers who need to understand narrative threats, response patterns, and investigation workflows before a crisis peaks.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-6 mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="rounded-3xl border border-zinc-800/60 bg-[linear-gradient(135deg,rgba(10,18,24,0.95),rgba(7,40,35,0.92),rgba(8,19,31,0.95))] p-8 md:p-10 overflow-hidden relative">
              <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.18),transparent_60%)] pointer-events-none" />
              <div className="relative z-10 max-w-xl">
                <p className="text-xs font-bold tracking-[0.22em] text-emerald-400 uppercase mb-4">Learning Tracks</p>
                <h3 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">Train teams to recognize, assess, and respond to narrative pressure with speed.</h3>
                <p className="text-zinc-300 leading-relaxed mb-8">
                  Instead of a generic blog menu, this section turns your landing page into a mission-education surface: practical guidance, explainers, response playbooks, and field examples mapped to the way your users actually work.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Operator playbooks', value: '12+' },
                    { label: 'Threat brief formats', value: '6' },
                    { label: 'Team onboarding paths', value: 'Role-based' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
                      <div className="text-lg font-extrabold text-white">{item.value}</div>
                      <div className="text-xs text-zinc-400 mt-1">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-3xl border border-zinc-800/60 bg-zinc-950/80 p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-2xl bg-cyan-500/10 border border-cyan-500/20">
                  <Layers3 className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Recommended content structure</p>
                  <p className="text-xs text-zinc-500">Original, product-specific resource architecture</p>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  'Executive explainers that translate narrative risk into business, policy, and public-safety impact.',
                  'Analyst guides showing how to use signal scores, regional context, and evidence trails during active incidents.',
                  'Response playbooks for coordinated campaigns, breaking rumors, manipulated media, and reputational attacks.',
                  'Post-incident case notes documenting what was detected, how it spread, and which interventions worked.',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-2xl border border-zinc-800/60 bg-zinc-900/50 p-4">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-zinc-300 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {LEARN_RESOURCES.map((resource) => {
              const Icon = resource.icon;
              return (
                <motion.div key={resource.title} variants={fadeUp}
                  className="group rounded-2xl border border-zinc-800/60 bg-zinc-900/35 hover:bg-zinc-900/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_70px_-24px_rgba(16,185,129,0.28)]">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-500">{resource.tag}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-emerald-300 transition-colors">{resource.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-5">{resource.desc}</p>
                  <button onClick={() => setSelectedLearnResource(resource)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-colors">
                    {resource.linkLabel}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative py-16 px-4 md:py-32 md:px-6" id="pricing">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.08),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(244,63,94,0.07),transparent_28%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}
            className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold tracking-wider mb-6">
              <Briefcase className="w-3.5 h-3.5" /> PRICING FRAMEWORK
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
              Pricing shaped around <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-rose-300 to-cyan-300">mission scope</span>, not vanity tiers
            </h2>
            <p className="text-lg text-zinc-400 max-w-3xl mx-auto">
              CrisisShield deployments vary by source volume, monitoring geography, analyst count, and the intelligence suites you activate. The pricing conversation should reflect that reality, so the page is structured around buying scenarios rather than generic monthly boxes.
            </p>
          </motion.div>

          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            {(
              Object.keys(AUDIENCE_FOCUS) as Array<keyof typeof AUDIENCE_FOCUS>
            ).map((key) => (
              <button
                key={key}
                onClick={() => setAudienceFocus(key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  audienceFocus === key
                    ? 'bg-white text-zinc-950 shadow-[0_0_20px_rgba(255,255,255,0.15)]'
                    : 'bg-zinc-900/60 border border-zinc-800 text-zinc-400 hover:text-white'
                }`}
              >
                {key === 'government' ? 'Government' : key === 'enterprise' ? 'Enterprise' : 'Media'}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6 mb-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="grid md:grid-cols-3 gap-6">
              {PRICING_PLANS.map((plan) => (
                <div key={plan.tier} className={`relative rounded-3xl border ${plan.ring} bg-zinc-950/85 p-7 overflow-hidden`}>
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${plan.accent}`} />
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div>
                      <p className="text-2xl font-extrabold text-white">{plan.tier}</p>
                      <p className="text-sm text-zinc-500 mt-1">{plan.audience}</p>
                    </div>
                    <span className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-bold tracking-[0.16em] uppercase text-zinc-300">{plan.badge}</span>
                  </div>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-6">{plan.summary}</p>
                  <div className="space-y-3 mb-7">
                    {plan.includes.map((item) => (
                      <div key={item} className="flex items-start gap-3 text-sm text-zinc-400">
                        <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setPricingModalOpen(true)}
                    className={`w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r ${plan.accent} hover:opacity-90 transition-opacity`}>
                    Talk to the team
                  </button>
                </div>
              ))}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-3xl border border-zinc-800/60 bg-zinc-900/45 p-8 md:p-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-wider mb-6">
                <Shield className="w-3.5 h-3.5" /> WHAT DRIVES PRICE
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-5 leading-tight">{AUDIENCE_FOCUS[audienceFocus].title}</h3>
              <p className="text-sm text-zinc-300 leading-relaxed mb-6">{AUDIENCE_FOCUS[audienceFocus].body}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {AUDIENCE_FOCUS[audienceFocus].checklist.map((item) => (
                  <span key={item} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-zinc-300">
                    {item}
                  </span>
                ))}
              </div>
              <div className="space-y-4 mb-8">
                {[
                  { label: 'Monitoring footprint', desc: 'Number of sources, languages, tracked entities, and regional coverage rules.' },
                  { label: 'Operating model', desc: 'How many analyst seats, escalation paths, and admin workflows must be supported.' },
                  { label: 'Intelligence depth', desc: 'Which OSINT, forensic, and reporting modules are part of the deployment.' },
                  { label: 'Delivery model', desc: 'Whether you need a fast SaaS rollout, guided onboarding, or a tailored enterprise configuration.' },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-zinc-800/60 bg-zinc-950/60 p-4">
                    <p className="text-sm font-bold text-white mb-1">{item.label}</p>
                    <p className="text-sm text-zinc-400 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-5 mb-6">
                <p className="text-sm font-semibold text-amber-200 leading-relaxed">
                  Best fit for this page: position pricing as consultation-led, not self-serve checkout. That matches the product and avoids promising rigid numbers before deployment scope is known.
                </p>
              </div>
              <button onClick={() => setPricingModalOpen(true)}
                className="w-full py-3.5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 font-bold hover:bg-cyan-500/15 transition-colors">
                Request pricing conversation
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative py-16 px-4 md:py-32 md:px-6 bg-zinc-900/20" id="about">
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cyan-500/5 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-wider mb-6">
                <Globe className="w-3.5 h-3.5" /> ABOUT THE PLATFORM
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-8 leading-[1.15]">
                Built for the <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">information age</span>
              </h2>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                CrisisShield Intelligence was engineered to address the fastest-growing threat to democratic societies: the weaponization of information. Our platform combines cutting-edge artificial intelligence with real-time data streaming to detect, classify, and neutralize misinformation at scale.
              </p>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Purpose-built for government agencies, newsrooms, research institutions, and enterprise security teams, the system processes millions of social media posts daily across multiple languages, identifying coordinated manipulation campaigns before they reach critical mass.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-10">
                {[
                  '6-Suite OSINT waterfall pipeline',
                  'Transformer-based NLP ensemble',
                  'PostgreSQL 18.3 persistent analytics',
                  'Kafka real-time streaming',
                  'JWT enterprise security',
                  'Playwright visual regression CI',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" /> {item}
                  </div>
                ))}
              </div>
              <button onClick={() => setPage('signup')} className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-base font-bold rounded-xl hover:opacity-90 transition-opacity flex items-center gap-2 group shadow-xl shadow-cyan-500/20">
                Start Monitoring <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}
              className="space-y-6">
              {[
                { icon: Activity, number: '01', title: 'Collect & Ingest', desc: 'Real-time data ingestion from Twitter/X, Reddit, Facebook, YouTube, and 500+ global news APIs via Apache Kafka streaming pipelines.' },
                { icon: Brain, number: '02', title: 'AI Classification', desc: 'Ensemble machine learning pipeline combining BERT transformers, sentiment analysis, bot detection, and risk scoring to classify every post in under 200ms.' },
                { icon: Target, number: '03', title: 'Threat Assessment', desc: 'Composite risk engine evaluates virality potential, bot amplification, source credibility, and network coordination to assign threat vectors.' },
                { icon: ShieldAlert, number: '04', title: 'Alert & Respond', desc: 'Automated alert generation with configurable severity thresholds. Email notifications, Slack integration, and real-time WebSocket dashboard updates.' },
              ].map(({ icon: Icon, number, title, desc }, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                  className="flex gap-5 p-6 rounded-2xl border border-zinc-800/50 bg-zinc-900/40 hover:bg-zinc-900/70 transition-all group">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-all overflow-hidden">
                      <motion.div
                        animate={([
                            { scaleY: [1, 1.3, 0.8, 1.1, 1] },
                            { rotate: [0, 360] },
                            { scale: [1, 1.35, 0.85, 1] },
                            { y: [0, -4, 0] },
                          ] as any[])[i]}
                        transition={{
                          repeat: Infinity,
                          duration: [1.4, 7, 1.2, 2][i],
                          ease: i === 1 ? 'linear' : 'easeInOut',
                        }}>
                        <Icon className="w-5 h-5 text-cyan-400" />
                      </motion.div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-bold text-cyan-500/60">{number}</span>
                      <h3 className="text-base font-bold text-white">{title}</h3>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform / Technology Section */}
      <section className="relative py-16 px-4 md:py-32 md:px-6" id="platform">
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-indigo-500/5 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.6 }}
            className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-bold tracking-wider mb-6">
              <Lock className="w-3.5 h-3.5" /> PLATFORM ARCHITECTURE
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
              Enterprise-grade <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">technology stack</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Built on proven, scalable infrastructure trusted by security teams worldwide. Every component designed for resilience, performance, and compliance.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Globe, title: 'Multi-Platform Collection', items: ['Twitter / X API v2', 'Reddit Data API', 'NewsAPI & GDELT', 'YouTube Data API', 'Facebook Graph API'], color: 'cyan' },
              { icon: Brain, title: 'AI & ML Pipeline', items: ['BERT Transformer Models', 'Ensemble Classification', 'Sentiment Analysis (VADER)', 'Bot Detection Network', 'Fine-tuned Risk Scoring'], color: 'purple' },
              { icon: BarChart3, title: 'Data Infrastructure', items: ['PostgreSQL 18.3 (Cloud SQL)', 'Apache Kafka Streaming', 'Redis In-Memory Cache', 'Google Cloud Run', 'Cloud Build + Playwright CI'], color: 'emerald' },
              { icon: Shield, title: 'Security & Compliance', items: ['JWT Authentication', 'Rate Limiting & WAF', 'CSP Security Headers', 'Audit Logging System', 'End-to-End Encryption'], color: 'rose' },
            ].map(({ icon: Icon, title, items, color }, i) => (
              <motion.div key={i} variants={fadeUp}
                className="p-8 rounded-2xl border border-zinc-800/50 bg-zinc-900/30 hover:bg-zinc-900/50 transition-all group">
                <div className={`p-3 rounded-xl bg-${color}-500/10 border border-${color}-500/20 w-fit mb-6 overflow-hidden`}>
                  <motion.div
                    animate={([
                        { rotate: [0, 360] },
                        { scale: [1, 1.22, 1] },
                        { y: [0, -4, 0] },
                        { scale: [1, 1.18, 0.94, 1] },
                      ] as any[])[i]}
                    transition={{
                      repeat: Infinity,
                      duration: [11, 2.2, 2.8, 2][i],
                      ease: i === 0 ? 'linear' : 'easeInOut',
                    }}>
                    <Icon className={`w-6 h-6 text-${color}-400`} />
                  </motion.div>
                </div>
                <h3 className="text-lg font-bold text-white mb-5">{title}</h3>
                <ul className="space-y-3">
                  {items.map((item, j) => (
                    <li key={j} className="flex items-center gap-3 text-sm text-zinc-400">
                      <span className={`w-1.5 h-1.5 rounded-full bg-${color}-400/60 flex-shrink-0`} /> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 px-4 md:py-32 md:px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold tracking-wider mb-8">
              <Award className="w-3.5 h-3.5" /> TRUSTED WORLDWIDE
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tight mb-8 leading-[1.1]">
              Ready to protect your <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">information ecosystem?</span>
            </h2>
            <p className="text-lg text-zinc-400 mb-12 max-w-2xl mx-auto">
              Join intelligence agencies, newsrooms, and research institutions that rely on CrisisShield Intelligence to safeguard truth in the digital age. Deploy in minutes with our cloud-native architecture.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button onClick={() => setPage('signup')} className="px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-bold rounded-xl hover:opacity-90 transition-opacity flex items-center gap-3 group shadow-2xl shadow-cyan-500/30">
                Deploy CrisisShield <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => setDemoModalOpen(true)} className="px-10 py-5 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-white text-lg font-bold rounded-xl transition-colors">
                Request Demo
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-12 text-sm text-zinc-500">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-cyan-500" /> No credit card required</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-cyan-500" /> SOC 2 Compliant</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-cyan-500" /> 99.9% Uptime SLA</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/50 bg-zinc-950 relative z-10 pt-20 pb-8" id="contact">
        <div className="max-w-7xl mx-auto px-6">
          {/* Main Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <motion.div
                  className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg"
                  animate={{
                    scale: [1, 1.12, 1],
                    boxShadow: [
                      '0 0 14px rgba(6,182,212,0.45)',
                      '0 0 30px rgba(6,182,212,0.75)',
                      '0 0 14px rgba(6,182,212,0.45)',
                    ],
                  }}
                  transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}>
                  <ShieldAlert className="w-5 h-5 text-white" />
                </motion.div>
                <div>
                  <span className="text-lg font-bold text-white">CrisisShield Intelligence</span>
                  <p className="text-xs text-zinc-500 tracking-widest uppercase">Private Limited</p>
                </div>
              </div>
              <p className="text-zinc-400 text-[15px] leading-relaxed max-w-sm mb-6">
                Protecting global truth through advanced artificial intelligence, distributed network analysis, and real-time threat intelligence. Registered in India &mdash; trusted by organizations in 30+ countries.
              </p>
              {/* Startup India (DPIIT) recognition */}
              <div className="inline-flex items-center gap-3 sm:gap-3.5 pl-2.5 pr-4 py-2.5 mb-8 max-w-full rounded-2xl bg-white border border-white/40 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.5)]">
                <img src="/assets/startupindia.jpeg" alt="Startup India — DPIIT recognized"
                  className="h-12 sm:h-14 w-auto object-contain select-none shrink-0" draggable={false} />
                <div className="leading-snug min-w-0 border-l border-zinc-200 pl-3.5">
                  <p className="text-[13px] font-extrabold tracking-wide text-zinc-900 uppercase">Recognized Startup</p>
                  <p className="text-[12px] font-semibold text-zinc-700">DPIIT &middot; <span className="text-emerald-600">Startup India</span></p>
                  <p className="text-[11px] font-medium text-zinc-500">Government of India</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a href="#" className="p-2.5 bg-zinc-900 border border-zinc-800 hover:text-cyan-400 hover:border-cyan-500/30 hover:bg-zinc-800 rounded-xl transition-all text-zinc-400"><Twitter className="w-4 h-4" /></a>
                <a href="https://www.linkedin.com/company/132044052" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-zinc-900 border border-zinc-800 hover:text-cyan-400 hover:border-cyan-500/30 hover:bg-zinc-800 rounded-xl transition-all text-zinc-400"><Linkedin className="w-4 h-4" /></a>
                <a href="https://github.com/abdinav007/Crisis-Misinformation-Monitoring-System" target="_blank" rel="noopener noreferrer" className="p-2.5 bg-zinc-900 border border-zinc-800 hover:text-cyan-400 hover:border-cyan-500/30 hover:bg-zinc-800 rounded-xl transition-all text-zinc-400"><Github className="w-4 h-4" /></a>
                <a href="mailto:info@factcheckanalytics.com" className="p-2.5 bg-zinc-900 border border-zinc-800 hover:text-cyan-400 hover:border-cyan-500/30 hover:bg-zinc-800 rounded-xl transition-all text-zinc-400"><Mail className="w-4 h-4" /></a>
              </div>
            </div>

            {/* Platform Column */}
            <div>
              <h4 className="text-white font-bold text-base mb-6 tracking-wide">Platform</h4>
              <ul className="space-y-3 text-[15px] text-zinc-400">
                <li><a href="#features" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Radar className="w-3.5 h-3.5" /> Real-Time Monitoring</a></li>
                <li><a href="#osint" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><FileSearch className="w-3.5 h-3.5" /> 6-Suite OSINT Pipeline</a></li>
                <li><a href="#features" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Network className="w-3.5 h-3.5" /> Network Analysis</a></li>
                <li><a href="#features" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Risk Assessment</a></li>
                <li><a href="#features" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Bot className="w-3.5 h-3.5" /> Bot Detection</a></li>
                <li><a href="#features" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Brand Protection</a></li>
                <li><a href="#features" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5" /> Business Intelligence</a></li>
                <li><a href="#features" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Eye className="w-3.5 h-3.5" /> Neural AI Forensics</a></li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h4 className="text-white font-bold text-base mb-6 tracking-wide">Resources</h4>
              <ul className="space-y-3 text-[15px] text-zinc-400">
                <li><a href="#platform" className="hover:text-cyan-400 transition-colors">Documentation</a></li>
                <li><a href="#platform" className="hover:text-cyan-400 transition-colors">API Reference</a></li>
                <li><a href="#about" className="hover:text-cyan-400 transition-colors">Research Papers</a></li>
                <li><a href="#contact" className="hover:text-cyan-400 transition-colors">Status Page</a></li>
                <li><a href="https://github.com/abdinav007/Crisis-Misinformation-Monitoring-System" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Changelog</a></li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-white font-bold text-base mb-6 tracking-wide">Company</h4>
              <ul className="space-y-3 text-[15px] text-zinc-400">
                <li><a href="#about" className="hover:text-cyan-400 transition-colors">About Us</a></li>
                <li><a href="mailto:careers@factcheckanalytics.com" className="hover:text-cyan-400 transition-colors">Careers</a></li>
                <li><a href="mailto:info@factcheckanalytics.com" className="hover:text-cyan-400 transition-colors">Contact</a></li>
                <li><a href="#contact" className="hover:text-cyan-400 transition-colors">Privacy Policy</a></li>
                <li><a href="#contact" className="hover:text-cyan-400 transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-zinc-800/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-zinc-400">&copy; 2026 CrisisShield Intelligence Private Limited. All rights reserved.</p>
            <div className="flex items-center gap-6 text-sm text-zinc-400">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> All Systems Operational</span>
              <span>v2.4.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
